import React, { useEffect, useState } from 'react';

interface ClassificationDomain { id: number; domain: string; matchType: string; category: string; notes: string | null; source: string; }
interface ClassificationSender { id: number; senderEmail: string; category: string; notes: string | null; }
interface ClassificationCandidate { id: number; candidateType: string; value: string; category: string; confidence: number; seenCount: number; lastSeenAt: string; status: string; }

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export default function AdminClassificationPage() {
  const [classificationTab, setClassificationTab] = useState<'domains' | 'senders' | 'candidates'>('domains');

  const [domains, setDomains] = useState<ClassificationDomain[]>([]);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [senders, setSenders] = useState<ClassificationSender[]>([]);
  const [sendersLoaded, setSendersLoaded] = useState(false);
  const [candidates, setCandidates] = useState<ClassificationCandidate[]>([]);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);

  const [newDomain, setNewDomain] = useState('');
  const [newDomainMatchType, setNewDomainMatchType] = useState('suffix');
  const [newDomainCategory, setNewDomainCategory] = useState('subscription');
  const [newDomainNotes, setNewDomainNotes] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [addDomainMsg, setAddDomainMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [newSenderCategory, setNewSenderCategory] = useState('subscription');
  const [newSenderNotes, setNewSenderNotes] = useState('');
  const [addingSender, setAddingSender] = useState(false);
  const [addSenderMsg, setAddSenderMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    loadDomains();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDomains() {
    const res = await fetch('/api/mgmt/classification/domains');
    if (res.ok) setDomains(await res.json());
    setDomainsLoaded(true);
  }

  async function loadSenders() {
    const res = await fetch('/api/mgmt/classification/senders');
    if (res.ok) setSenders(await res.json());
    setSendersLoaded(true);
  }

  async function loadCandidates() {
    const res = await fetch('/api/mgmt/classification/candidates');
    if (res.ok) setCandidates(await res.json());
    setCandidatesLoaded(true);
  }

  function switchTab(tab: 'domains' | 'senders' | 'candidates') {
    setClassificationTab(tab);
    if (tab === 'senders' && !sendersLoaded) loadSenders();
    if (tab === 'candidates' && !candidatesLoaded) loadCandidates();
  }

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    setAddDomainMsg(null);
    try {
      const res = await fetch('/api/mgmt/classification/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim(), matchType: newDomainMatchType, category: newDomainCategory, notes: newDomainNotes.trim() || null }),
      });
      if (res.ok) {
        setAddDomainMsg({ ok: true, text: 'Domain rule added.' });
        setNewDomain(''); setNewDomainNotes('');
        await loadDomains();
      } else {
        const data = await res.json().catch(() => ({}));
        setAddDomainMsg({ ok: false, text: data.error ?? 'Add failed.' });
      }
    } catch {
      setAddDomainMsg({ ok: false, text: 'Add failed.' });
    } finally {
      setAddingDomain(false);
    }
  }

  async function handleDeleteDomain(id: number) {
    await fetch(`/api/mgmt/classification/domains/${id}`, { method: 'DELETE' });
    setDomains(prev => prev.filter(d => d.id !== id));
  }

  async function handleAddSender(e: React.FormEvent) {
    e.preventDefault();
    if (!newSenderEmail.trim()) return;
    setAddingSender(true);
    setAddSenderMsg(null);
    try {
      const res = await fetch('/api/mgmt/classification/senders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail: newSenderEmail.trim(), category: newSenderCategory, notes: newSenderNotes.trim() || null }),
      });
      if (res.ok) {
        setAddSenderMsg({ ok: true, text: 'Sender rule added.' });
        setNewSenderEmail(''); setNewSenderNotes('');
        await loadSenders();
      } else {
        const data = await res.json().catch(() => ({}));
        setAddSenderMsg({ ok: false, text: data.error ?? 'Add failed.' });
      }
    } catch {
      setAddSenderMsg({ ok: false, text: 'Add failed.' });
    } finally {
      setAddingSender(false);
    }
  }

  async function handleDeleteSender(id: number) {
    await fetch(`/api/mgmt/classification/senders/${id}`, { method: 'DELETE' });
    setSenders(prev => prev.filter(s => s.id !== id));
  }

  async function handlePromoteCandidate(id: number) {
    await fetch(`/api/mgmt/classification/candidates/${id}/promote`, { method: 'POST' });
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, status: 'promoted' } : c));
    setDomainsLoaded(false);
  }

  async function handleDismissCandidate(id: number) {
    await fetch(`/api/mgmt/classification/candidates/${id}/dismiss`, { method: 'POST' });
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, status: 'dismissed' } : c));
  }

  return (
    <>
      <div className="page-header">
        <h1>Classification</h1>
      </div>

      <div className="dashboard-card">
        <div className="card-label" style={{ marginBottom: 12 }}>Classification rules</div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {(['domains', 'senders', 'candidates'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => switchTab(tab)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '6px 14px', fontSize: 13,
                fontWeight: classificationTab === tab ? 600 : 400,
                color: classificationTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
                borderBottom: classificationTab === tab ? '2px solid var(--text-primary)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {tab === 'domains' ? `Domains (${domains.length})` : tab === 'senders' ? `Senders (${senders.length})` : 'LLM candidates'}
            </button>
          ))}
        </div>

        {/* Domain rules tab */}
        {classificationTab === 'domains' && (
          <div>
            {!domainsLoaded ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : domains.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>No domain rules yet.</p>
            ) : (
              <div style={{ overflowX: 'auto', marginBottom: 24 }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Domain</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Match</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Category</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Source</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Notes</th>
                      <th style={{ padding: '4px 0 8px 0', fontWeight: 500 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map(d => (
                      <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px 8px 0', fontFamily: 'monospace' }}>{d.domain}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{d.matchType}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{d.category}</td>
                        <td style={{ padding: '8px 12px 8px 0', color: d.source === 'llm_auto' ? 'var(--text-secondary)' : undefined }}>{d.source ?? 'manual'}</td>
                        <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-secondary)' }}>{d.notes ?? '—'}</td>
                        <td style={{ padding: '8px 0' }}>
                          <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--danger)', padding: '2px 8px' }} onClick={() => handleDeleteDomain(d.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Add domain rule</div>
            <form onSubmit={handleAddDomain} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Domain</div>
                <input className="search-input" type="text" placeholder="e.g. mailchimp.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} style={{ width: 200 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Match type</div>
                <select className="search-input" value={newDomainMatchType} onChange={e => setNewDomainMatchType(e.target.value)}>
                  <option value="suffix">suffix</option>
                  <option value="exact">exact</option>
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Category</div>
                <select className="search-input" value={newDomainCategory} onChange={e => setNewDomainCategory(e.target.value)}>
                  {['subscription', 'transactional', 'notification', 'marketing', 'personal'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Notes (optional)</div>
                <input className="search-input" type="text" placeholder="Optional" value={newDomainNotes} onChange={e => setNewDomainNotes(e.target.value)} style={{ width: 160 }} />
              </label>
              <button className="btn-primary" type="submit" disabled={addingDomain || !newDomain.trim()} style={{ alignSelf: 'flex-end' }}>
                {addingDomain ? 'Adding…' : 'Add rule'}
              </button>
            </form>
            {addDomainMsg && (
              <p style={{ marginTop: 8, fontSize: 13, color: addDomainMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{addDomainMsg.text}</p>
            )}
          </div>
        )}

        {/* Sender rules tab */}
        {classificationTab === 'senders' && (
          <div>
            {!sendersLoaded ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : senders.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>No sender rules yet.</p>
            ) : (
              <div style={{ overflowX: 'auto', marginBottom: 24 }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Sender email</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Category</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Notes</th>
                      <th style={{ padding: '4px 0 8px 0', fontWeight: 500 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {senders.map(s => (
                      <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px 8px 0', fontFamily: 'monospace' }}>{s.senderEmail}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{s.category}</td>
                        <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-secondary)' }}>{s.notes ?? '—'}</td>
                        <td style={{ padding: '8px 0' }}>
                          <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--danger)', padding: '2px 8px' }} onClick={() => handleDeleteSender(s.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Add sender rule</div>
            <form onSubmit={handleAddSender} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Sender email</div>
                <input className="search-input" type="email" placeholder="e.g. news@example.com" value={newSenderEmail} onChange={e => setNewSenderEmail(e.target.value)} style={{ width: 240 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Category</div>
                <select className="search-input" value={newSenderCategory} onChange={e => setNewSenderCategory(e.target.value)}>
                  {['subscription', 'transactional', 'notification', 'marketing', 'personal'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Notes (optional)</div>
                <input className="search-input" type="text" placeholder="Optional" value={newSenderNotes} onChange={e => setNewSenderNotes(e.target.value)} style={{ width: 160 }} />
              </label>
              <button className="btn-primary" type="submit" disabled={addingSender || !newSenderEmail.trim()} style={{ alignSelf: 'flex-end' }}>
                {addingSender ? 'Adding…' : 'Add rule'}
              </button>
            </form>
            {addSenderMsg && (
              <p style={{ marginTop: 8, fontSize: 13, color: addSenderMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{addSenderMsg.text}</p>
            )}
          </div>
        )}

        {/* LLM candidates tab */}
        {classificationTab === 'candidates' && (
          <div>
            {!candidatesLoaded ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : candidates.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No candidates yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Value</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Category</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Confidence</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Seen</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Last seen</th>
                      <th style={{ padding: '4px 12px 8px 0', fontWeight: 500 }}>Status</th>
                      <th style={{ padding: '4px 0 8px 0', fontWeight: 500 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map(c => (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--border)', opacity: c.status !== 'pending' ? 0.5 : 1 }}>
                        <td style={{ padding: '8px 12px 8px 0', fontFamily: 'monospace' }}>{c.value}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{c.category}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{(c.confidence * 100).toFixed(0)}%</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{c.seenCount}</td>
                        <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-secondary)' }}>{formatDate(c.lastSeenAt)}</td>
                        <td style={{ padding: '8px 12px 8px 0' }}>{c.status}</td>
                        <td style={{ padding: '8px 0' }}>
                          {c.status === 'pending' && (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => handlePromoteCandidate(c.id)}>
                                Promote
                              </button>
                              <button className="btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => handleDismissCandidate(c.id)}>
                                Dismiss
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
